
import { useState } from 'react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Badge } from '@/components/ui/badge';
import { Calendar } from '@/components/ui/calendar';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Checkbox } from '@/components/ui/checkbox';
import { Avatar, AvatarFallback } from '@/components/ui/avatar';
import { 
  Plus, 
  Trash2, 
  Calendar as CalendarIcon, 
  User, 
  AlertTriangle,
  Clock,
  Check,
  X,
  Edit2,
  ChevronDown,
  ChevronUp
} from 'lucide-react';
import { format } from 'date-fns';
import { cn } from '@/lib/utils';
import { useChecklistItems, ChecklistItem } from '@/hooks/useChecklistItems';
import { useBoardContext } from '@/contexts/BoardContext';
import { isItemOverdue, isItemDueSoon } from '@/utils/enhancedChecklistUtils';

interface ChecklistMetadata {
  id: string;
  name: string;
}

interface DiscreteChecklistManagerProps {
  cardId: string;
}

// Type guard to check if data is ChecklistMetadata[]
const isChecklistMetadataArray = (data: any): data is ChecklistMetadata[] => {
  return Array.isArray(data) && data.every(item => 
    typeof item === 'object' && 
    item !== null && 
    typeof item.id === 'string' && 
    typeof item.name === 'string'
  );
};

// Safe conversion function with backwards compatibility
const safeGetChecklistMetadata = (checklists: any, existingItems: ChecklistItem[]): ChecklistMetadata[] => {
  if (!checklists) {
    // If no metadata exists but we have items, infer metadata from existing checklist items
    if (existingItems && existingItems.length > 0) {
      const uniqueChecklistIds = [...new Set(existingItems.map(item => item.checklist_id))];
      return uniqueChecklistIds.map(id => ({
        id,
        name: `Checklist ${id.slice(-4)}` // Default name using last 4 chars of ID
      }));
    }
    return [];
  }
  
  // If it's already an array and matches our interface, return it
  if (isChecklistMetadataArray(checklists)) {
    return checklists;
  }
  
  // If it's a JSON string, try to parse it
  if (typeof checklists === 'string') {
    try {
      const parsed = JSON.parse(checklists);
      if (isChecklistMetadataArray(parsed)) {
        return parsed;
      }
    } catch {
      // If parsing fails, fall back to inferring from existing items
    }
  }
  
  // Fallback: infer from existing items if available
  if (existingItems && existingItems.length > 0) {
    const uniqueChecklistIds = [...new Set(existingItems.map(item => item.checklist_id))];
    return uniqueChecklistIds.map(id => ({
      id,
      name: `Checklist ${id.slice(-4)}`
    }));
  }
  
  return [];
};

export const DiscreteChecklistManager = ({ cardId }: DiscreteChecklistManagerProps) => {
  const { profiles, cards, updateCard } = useBoardContext();
  const { 
    checklistItems, 
    createChecklistItem, 
    updateChecklistItem, 
    deleteChecklistItem,
    isCreating,
    isUpdating,
    isDeleting
  } = useChecklistItems(cardId);

  const [newChecklistName, setNewChecklistName] = useState('');
  const [isAddingChecklist, setIsAddingChecklist] = useState(false);

  // Get current card and its checklist metadata with backwards compatibility
  const currentCard = cards?.find(card => card.id === cardId);
  const checklistMetadata: ChecklistMetadata[] = safeGetChecklistMetadata(currentCard?.checklists, checklistItems);

  // Group items by checklist_id
  const groupedChecklists = checklistItems.reduce((acc, item) => {
    if (!acc[item.checklist_id]) {
      acc[item.checklist_id] = [];
    }
    acc[item.checklist_id].push(item);
    return acc;
  }, {} as Record<string, ChecklistItem[]>);

  const generateChecklistId = () => Date.now().toString() + Math.random().toString(36).substr(2, 9);

  const getChecklistName = (checklistId: string): string => {
    const metadata = checklistMetadata.find(cl => cl.id === checklistId);
    return metadata?.name || 'Unnamed Checklist';
  };

  const addChecklist = async () => {
    if (newChecklistName.trim()) {
      const checklistId = generateChecklistId();
      
      // Update card metadata with new checklist
      const updatedMetadata = [...checklistMetadata, { id: checklistId, name: newChecklistName.trim() }];
      
      console.log('Adding checklist:', { checklistId, updatedMetadata });
      
      try {
        await updateCard({
          cardId,
          updates: { checklists: JSON.stringify(updatedMetadata) }
        });

        // Create first checklist item
        createChecklistItem({
          card_id: cardId,
          checklist_id: checklistId,
          text: 'Add your first task...',
          position: 0,
        });
        
        setNewChecklistName('');
        setIsAddingChecklist(false);
        console.log('Checklist added successfully');
      } catch (error) {
        console.error('Error adding checklist:', error);
      }
    }
  };

  const deleteChecklist = async (checklistId: string) => {
    // Remove checklist metadata
    const updatedMetadata = checklistMetadata.filter(cl => cl.id !== checklistId);
    
    console.log('Deleting checklist:', { checklistId, updatedMetadata });
    
    try {
      await updateCard({
        cardId,
        updates: { checklists: JSON.stringify(updatedMetadata) }
      });

      // Delete all items in this checklist
      const items = groupedChecklists[checklistId] || [];
      items.forEach(item => deleteChecklistItem(item.id));
      console.log('Checklist deleted successfully');
    } catch (error) {
      console.error('Error deleting checklist:', error);
    }
  };

  const renameChecklist = async (checklistId: string, newName: string) => {
    if (!newName.trim()) return;
    
    const updatedMetadata = checklistMetadata.map(cl => 
      cl.id === checklistId ? { ...cl, name: newName.trim() } : cl
    );

      // Check if the ID was found; if not, add it
      const idExists = checklistMetadata.some(cl => cl.id === checklistId);

      if (!idExists) {
          updatedMetadata.push({
              id: checklistId,
              name: newName.trim()
          });
      }

    console.log('Renaming checklist:', { checklistId, newName, updatedMetadata });
    
    try {
      await updateCard({
        cardId,
        updates: { checklists: JSON.stringify(updatedMetadata) }
      });
      console.log('Checklist renamed successfully');
    } catch (error) {
      console.error('Error renaming checklist:', error);
    }
  };

  const getChecklistProgress = (items: ChecklistItem[]) => {
    if (items.length === 0) return 0;
    const completed = items.filter(item => item.completed).length;
    return Math.round((completed / items.length) * 100);
  };

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <h4 className="font-medium">Checklists</h4>
        {!isAddingChecklist && (
          <Button
            type="button"
            variant="outline"
            size="sm"
            onClick={() => setIsAddingChecklist(true)}
          >
            <Plus className="h-4 w-4 mr-1" />
            Add Checklist
          </Button>
        )}
      </div>

      {isAddingChecklist && (
        <div className="flex gap-2">
          <Input
            placeholder="Checklist name"
            value={newChecklistName}
            onChange={(e) => setNewChecklistName(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === 'Enter') addChecklist();
              if (e.key === 'Escape') {
                setIsAddingChecklist(false);
                setNewChecklistName('');
              }
            }}
            autoFocus
          />
          <Button type="button" size="sm" onClick={addChecklist} disabled={isCreating}>
            <Check className="h-4 w-4" />
          </Button>
          <Button 
            type="button" 
            variant="ghost" 
            size="sm" 
            onClick={() => {
              setIsAddingChecklist(false);
              setNewChecklistName('');
            }}
          >
            <X className="h-4 w-4" />
          </Button>
        </div>
      )}

      {Object.entries(groupedChecklists).map(([checklistId, items]) => (
        <DiscreteChecklistComponent
          key={checklistId}
          checklistId={checklistId}
          checklistName={getChecklistName(checklistId)}
          items={items}
          cardId={cardId}
          profiles={profiles || []}
          onDelete={() => deleteChecklist(checklistId)}
          onRename={(newName) => renameChecklist(checklistId, newName)}
          onCreateItem={createChecklistItem}
          onUpdateItem={updateChecklistItem}
          onDeleteItem={deleteChecklistItem}
          progress={getChecklistProgress(items)}
          isCreating={isCreating}
          isUpdating={isUpdating}
          isDeleting={isDeleting}
        />
      ))}
    </div>
  );
};

interface DiscreteChecklistComponentProps {
  checklistId: string;
  checklistName: string;
  items: ChecklistItem[];
  cardId: string;
  profiles: any[];
  onDelete: () => void;
  onRename: (newName: string) => void;
  onCreateItem: (data: any) => void;
  onUpdateItem: (data: any) => void;
  onDeleteItem: (itemId: string) => void;
  progress: number;
  isCreating: boolean;
  isUpdating: boolean;
  isDeleting: boolean;
}

const DiscreteChecklistComponent = ({
  checklistId,
  checklistName,
  items,
  cardId,
  profiles,
  onDelete,
  onRename,
  onCreateItem,
  onUpdateItem,
  onDeleteItem,
  progress,
  isCreating,
  isUpdating,
  isDeleting
}: DiscreteChecklistComponentProps) => {
  const [newItemText, setNewItemText] = useState('');
  const [isAddingItem, setIsAddingItem] = useState(false);
  const [editingItemId, setEditingItemId] = useState<string | null>(null);
  const [editingText, setEditingText] = useState('');
  const [isEditingName, setIsEditingName] = useState(false);
  const [editingName, setEditingName] = useState(checklistName);

  const addItem = () => {
    if (newItemText.trim()) {
      onCreateItem({
        card_id: cardId,
        checklist_id: checklistId,
        text: newItemText,
        position: items.length,
      });
      setNewItemText('');
      setIsAddingItem(false);
    }
  };

  const startEditing = (item: ChecklistItem) => {
    setEditingItemId(item.id);
    setEditingText(item.text);
  };

  const saveEdit = () => {
    if (editingItemId && editingText.trim()) {
      onUpdateItem({
        itemId: editingItemId,
        updates: { text: editingText.trim() }
      });
    }
    setEditingItemId(null);
    setEditingText('');
  };

  const saveNameEdit = () => {
    if (editingName.trim()) {
      onRename(editingName.trim());
    }
    setIsEditingName(false);
  };

  const getPriorityColor = (priority: string) => {
    switch (priority) {
      case 'high': return 'bg-red-500';
      case 'medium': return 'bg-orange-500';
      case 'low': return 'bg-green-500';
      default: return 'bg-gray-500';
    }
  };

  return (
    <div className="border rounded-lg p-4 space-y-3">
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-2">
          {isEditingName ? (
            <div className="flex gap-2">
              <Input
                value={editingName}
                onChange={(e) => setEditingName(e.target.value)}
                onKeyDown={(e) => {
                  if (e.key === 'Enter') saveNameEdit();
                  if (e.key === 'Escape') {
                    setIsEditingName(false);
                    setEditingName(checklistName);
                  }
                }}
                className="text-sm font-medium"
                autoFocus
              />
              <Button type="button" size="sm" onClick={saveNameEdit}>
                <Check className="h-3 w-3" />
              </Button>
              <Button 
                type="button" 
                variant="ghost" 
                size="sm" 
                onClick={() => {
                  setIsEditingName(false);
                  setEditingName(checklistName);
                }}
              >
                <X className="h-3 w-3" />
              </Button>
            </div>
          ) : (
            <>
              <h5 
                className="font-medium cursor-pointer hover:text-primary"
                onClick={() => setIsEditingName(true)}
              >
                {checklistName}
              </h5>
              <Button
                type="button"
                variant="ghost"
                size="sm"
                onClick={() => setIsEditingName(true)}
                className="h-6 w-6 p-0"
              >
                <Edit2 className="h-3 w-3" />
              </Button>
            </>
          )}
          <Badge variant="secondary">
            {progress}% ({items.filter(i => i.completed).length}/{items.length})
          </Badge>
        </div>
        
        <Button
          type="button"
          variant="ghost"
          size="sm"
          onClick={onDelete}
          className="h-6 w-6 p-0 text-red-500 hover:text-red-700"
        >
          <Trash2 className="h-3 w-3" />
        </Button>
      </div>

      {items.length > 0 && (
        <div className="space-y-2">
          {items.map((item) => (
            <DiscreteChecklistItem
              key={item.id}
              item={item}
              profiles={profiles}
              onUpdate={onUpdateItem}
              onDelete={() => onDeleteItem(item.id)}
              onStartEditing={() => startEditing(item)}
              isEditing={editingItemId === item.id}
              editingText={editingText}
              onEditingTextChange={setEditingText}
              onSaveEdit={saveEdit}
              onCancelEdit={() => {
                setEditingItemId(null);
                setEditingText('');
              }}
            />
          ))}
        </div>
      )}

      {isAddingItem ? (
        <div className="flex gap-2">
          <Input
            placeholder="Add item..."
            value={newItemText}
            onChange={(e) => setNewItemText(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === 'Enter') addItem();
              if (e.key === 'Escape') {
                setIsAddingItem(false);
                setNewItemText('');
              }
            }}
            autoFocus
          />
          <Button type="button" size="sm" onClick={addItem} disabled={isCreating}>
            <Check className="h-4 w-4" />
          </Button>
          <Button 
            type="button" 
            variant="ghost" 
            size="sm" 
            onClick={() => {
              setIsAddingItem(false);
              setNewItemText('');
            }}
          >
            <X className="h-4 w-4" />
          </Button>
        </div>
      ) : (
        <Button
          type="button"
          variant="ghost"
          size="sm"
          onClick={() => setIsAddingItem(true)}
          className="w-full justify-start text-muted-foreground"
        >
          <Plus className="h-4 w-4 mr-2" />
          Add item
        </Button>
      )}
    </div>
  );
};

interface DiscreteChecklistItemProps {
  item: ChecklistItem;
  profiles: any[];
  onUpdate: (data: any) => void;
  onDelete: () => void;
  onStartEditing: () => void;
  isEditing: boolean;
  editingText: string;
  onEditingTextChange: (text: string) => void;
  onSaveEdit: () => void;
  onCancelEdit: () => void;
}

const DiscreteChecklistItem = ({
  item,
  profiles,
  onUpdate,
  onDelete,
  onStartEditing,
  isEditing,
  editingText,
  onEditingTextChange,
  onSaveEdit,
  onCancelEdit
}: DiscreteChecklistItemProps) => {
  const [showAdvanced, setShowAdvanced] = useState(
    !!(item.assigned_to || item.due_date || item.priority !== 'medium')
  );

  const getPriorityColor = (priority: string) => {
    switch (priority) {
      case 'high': return 'bg-red-500';
      case 'medium': return 'bg-orange-500';
      case 'low': return 'bg-green-500';
      default: return 'bg-gray-500';
    }
  };

  return (
    <div className="group border rounded p-2 space-y-2">
      <div className="flex items-start gap-2">
        <Checkbox
          checked={item.completed}
          onCheckedChange={(checked) => 
            onUpdate({
              itemId: item.id,
              updates: { completed: !!checked }
            })
          }
          className="mt-0.5"
        />
        
        <div className="flex-1 space-y-1">
          {isEditing ? (
            <div className="flex gap-2">
              <Input
                value={editingText}
                onChange={(e) => onEditingTextChange(e.target.value)}
                onKeyDown={(e) => {
                  if (e.key === 'Enter') onSaveEdit();
                  if (e.key === 'Escape') onCancelEdit();
                }}
                className="text-sm"
                autoFocus
              />
              <Button type="button" size="sm" onClick={onSaveEdit}>
                <Check className="h-3 w-3" />
              </Button>
              <Button type="button" variant="ghost" size="sm" onClick={onCancelEdit}>
                <X className="h-3 w-3" />
              </Button>
            </div>
          ) : (
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-2 flex-1">
                <span 
                  className={cn(
                    "text-sm cursor-pointer flex-1",
                    item.completed && "line-through text-muted-foreground"
                  )}
                  onClick={onStartEditing}
                >
                  {item.text}
                </span>
                
                {/* Visual indicators */}
                <div className="flex items-center gap-1">
                  {/* Priority dot */}
                  {item.priority !== 'medium' && (
                    <div className={cn("h-2 w-2 rounded-full", getPriorityColor(item.priority))} />
                  )}
                  
                  {/* Overdue/due soon indicators */}
                  {isItemOverdue(item) && (
                    <AlertTriangle className="h-3 w-3 text-red-500" />
                  )}
                  {isItemDueSoon(item) && (
                    <Clock className="h-3 w-3 text-orange-500" />
                  )}
                  
                  {/* Assigned user avatar */}
                  {item.assigned_profile && (
                    <Avatar className="h-4 w-4">
                      <AvatarFallback className="text-xs">
                        {item.assigned_profile.full_name?.charAt(0) || 
                         item.assigned_profile.username?.charAt(0) || 
                         '?'}
                      </AvatarFallback>
                    </Avatar>
                  )}
                </div>
              </div>
              
              <div className="flex items-center gap-1 opacity-0 group-hover:opacity-100 transition-opacity">
                <Button
                  type="button"
                  variant="ghost"
                  size="sm"
                  onClick={() => setShowAdvanced(!showAdvanced)}
                  className="h-6 w-6 p-0"
                >
                  {showAdvanced ? (
                    <ChevronUp className="h-3 w-3" />
                  ) : (
                    <ChevronDown className="h-3 w-3" />
                  )}
                </Button>
                <Button
                  type="button"
                  variant="ghost"
                  size="sm"
                  onClick={onStartEditing}
                  className="h-6 w-6 p-0"
                >
                  <Edit2 className="h-3 w-3" />
                </Button>
                <Button
                  type="button"
                  variant="ghost"
                  size="sm"
                  onClick={onDelete}
                  className="h-6 w-6 p-0 text-red-500 hover:text-red-700"
                >
                  <Trash2 className="h-3 w-3" />
                </Button>
              </div>
            </div>
          )}

          {/* Advanced options - only show when expanded */}
          {showAdvanced && !isEditing && (
            <div className="flex gap-2 items-center pt-1">
              {/* Assignee selection */}
              <Select
                value={item.assigned_to || 'unassigned'}
                onValueChange={(value) => 
                  onUpdate({ 
                    itemId: item.id,
                    updates: { assigned_to: value === 'unassigned' ? null : value }
                  })
                }
              >
                <SelectTrigger className="h-6 text-xs w-24">
                  <SelectValue placeholder="Assign" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="unassigned">None</SelectItem>
                  {profiles.map((profile) => (
                    <SelectItem key={profile.id} value={profile.id}>
                      {profile.full_name || profile.username || 'Unnamed'}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>

              {/* Due date selection */}
              <Popover>
                <PopoverTrigger asChild>
                  <Button variant="outline" size="sm" className="h-6 text-xs">
                    <CalendarIcon className="h-3 w-3 mr-1" />
                    {item.due_date ? format(new Date(item.due_date), 'MMM d') : 'Due'}
                  </Button>
                </PopoverTrigger>
                <PopoverContent className="w-auto p-0" align="start">
                  <Calendar
                    mode="single"
                    selected={item.due_date ? new Date(item.due_date) : undefined}
                    onSelect={(date) => 
                      onUpdate({ 
                        itemId: item.id,
                        updates: { due_date: date ? format(date, 'yyyy-MM-dd') : null }
                      })
                    }
                    disabled={(date) => date < new Date('1900-01-01')}
                    initialFocus
                  />
                </PopoverContent>
              </Popover>

              {/* Priority selection */}
              <Select
                value={item.priority}
                onValueChange={(value: 'low' | 'medium' | 'high') => 
                  onUpdate({ 
                    itemId: item.id,
                    updates: { priority: value }
                  })
                }
              >
                <SelectTrigger className="h-6 text-xs w-16">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="low">Low</SelectItem>
                  <SelectItem value="medium">Med</SelectItem>
                  <SelectItem value="high">High</SelectItem>
                </SelectContent>
              </Select>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};
